import React from 'react';
import { Plus, Trash2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { crore, inputCls, inputBase } from './api';

const toCr = (v) => (v ? +(v / 1e7).toFixed(4) : '');
const fromCr = (s) => (s === '' ? 0 : Math.round(parseFloat(s) * 1e7) || 0);

export const CroreInput = ({ value, onChange, testId, className = '' }) => {
  const [text, setText] = React.useState(toCr(value));
  React.useEffect(() => {
    if (fromCr(text) !== (value || 0)) setText(toCr(value));
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className={`relative ${className}`}>
      <input type="number" step="0.01" min="0" value={text} onChange={e => { setText(e.target.value); onChange(fromCr(e.target.value)); }}
        className={`${inputCls} pr-10 text-right tabular-nums`} data-testid={testId} placeholder="0" />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">Cr</span>
    </div>
  );
};

export const SumCheck = ({ target, sum, label, testId }) => {
  const ok = Math.abs((target || 0) - (sum || 0)) < 1;
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${ok ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-800'}`}>
      {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
      {label}: {crore(sum)} {ok ? '' : `(${sum > target ? '+' : '−'}${crore(Math.abs(sum - target))})`}
    </span>
  );
};

const Q4 = (q) => [...(q || []), 0, 0, 0, 0].slice(0, 4);

export const QuarterInputs = ({ quarters, onChange, target, testPrefix, compact = false }) => {
  const q = Q4(quarters);
  const any = q.some(Boolean);
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`${testPrefix}-quarters`}>
      {q.map((v, i) => (
        <div key={i} className="flex items-center gap-1">
          <span className="text-[11px] font-semibold text-slate-400">Q{i + 1}</span>
          <CroreInput value={v} onChange={nv => onChange(q.map((x, j) => (j === i ? nv : x)))} className={compact ? 'w-24' : 'w-28'} testId={`${testPrefix}-q${i + 1}`} />
        </div>
      ))}
      {any ? <SumCheck target={target} sum={q.reduce((a, b) => a + b, 0)} label="Q1–Q4" testId={`${testPrefix}-qcheck`} /> : <span className="text-[11px] text-slate-400">Quarters not set</span>}
    </div>
  );
};

const PeopleRows = ({ bu, plan, setPlan, team }) => {
  const rows = plan.person_targets.map((p, i) => ({ ...p, i })).filter(p => p.bu_id === bu.id);
  const update = (i, patch) => setPlan({ ...plan, person_targets: plan.person_targets.map((p, j) => (j === i ? { ...p, ...patch } : p)) });
  const remove = (i) => setPlan({ ...plan, person_targets: plan.person_targets.filter((_, j) => j !== i) });
  const add = () => {
    const free = team.find(t => t.active && !rows.some(r => r.person_id === t.id));
    if (free) setPlan({ ...plan, person_targets: [...plan.person_targets, { person_id: free.id, bu_id: bu.id, target: 0 }] });
  };
  return (
    <div className="pl-6 py-2 space-y-2 bg-slate-50/60" data-testid={`person-targets-${bu.id}`}>
      {rows.map(r => (
        <div key={r.i} className="flex flex-wrap items-center gap-2">
          <select value={r.person_id} onChange={e => update(r.i, { person_id: e.target.value })} className={`${inputBase} w-56`} data-testid={`person-select-${bu.id}-${r.i}`}>
            {team.map(t => <option key={t.id} value={t.id} disabled={!t.active}>{t.name}{t.active ? '' : ' (disabled)'}</option>)}
          </select>
          <CroreInput value={r.target} onChange={v => update(r.i, { target: v })} className="w-32" testId={`person-target-${bu.id}-${r.i}`} />
          <QuarterInputs compact quarters={r.quarters} target={r.target} onChange={qs => update(r.i, { quarters: qs })} testPrefix={`person-${bu.id}-${r.i}`} />
          <button onClick={() => remove(r.i)} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`person-remove-${bu.id}-${r.i}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <button onClick={add} className="text-xs text-blue-600 hover:underline flex items-center gap-1" data-testid={`person-add-${bu.id}`}><Plus className="w-3 h-3" /> Salesperson target</button>
    </div>
  );
};

const BuTable = ({ company, bus, plan, setPlan, team }) => {
  const setBu = (id, patch) => setPlan({ ...plan, bu_targets: { ...plan.bu_targets, [id]: { target: 0, owner_id: null, ...(plan.bu_targets[id] || {}), ...patch } } });
  return (
    <div className="divide-y divide-slate-100">
      {bus.map(b => {
        const bt = plan.bu_targets[b.id] || {};
        const pSum = plan.person_targets.filter(p => p.bu_id === b.id).reduce((s, p) => s + (p.target || 0), 0);
        const hasPeople = plan.person_targets.some(p => p.bu_id === b.id);
        return (
          <div key={b.id} data-testid={`bu-target-row-${b.id}`}>
            <div className="flex flex-wrap items-center gap-3 px-4 py-2">
              <div className="flex-1 min-w-[180px]"><p className="text-sm font-medium text-slate-800">{b.name}</p><p className="text-xs text-slate-400 font-mono">{b.code}</p></div>
              <select value={bt.owner_id || ''} onChange={e => setBu(b.id, { owner_id: e.target.value || null })} className={`${inputBase} w-48`} data-testid={`bu-owner-${b.id}`}>
                <option value="">Target owner</option>
                {team.filter(t => t.active || t.id === bt.owner_id).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <CroreInput value={bt.target} onChange={v => setBu(b.id, { target: v })} className="w-36" testId={`bu-target-${b.id}`} />
              {hasPeople && <SumCheck target={bt.target} sum={pSum} label="Salespeople" testId={`bu-sumcheck-${b.id}`} />}
            </div>
            <div className="px-4 pb-2"><QuarterInputs quarters={bt.quarters} target={bt.target || 0} onChange={qs => setBu(b.id, { quarters: qs })} testPrefix={`bu-${b.id}`} /></div>
            <PeopleRows bu={b} plan={plan} setPlan={setPlan} team={team} />
          </div>
        );
      })}
      {bus.length === 0 && <p className="px-4 py-3 text-sm text-slate-400">No active business units in {company.name}</p>}
    </div>
  );
};

const UNITS = [{ v: 'amount', l: '₹' }, { v: 'percent', l: '%' }, { v: 'count', l: 'Count' }];

const KpiTable = ({ plan, setPlan }) => {
  const update = (i, patch) => setPlan({ ...plan, kpis: plan.kpis.map((k, j) => (j === i ? { ...k, ...patch } : k)) });
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="kpi-editor">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-800">Company Strategic KPIs</h3>
        <button onClick={() => setPlan({ ...plan, kpis: [...plan.kpis, { name: '', target: 0, unit: 'percent', basis: '', focus: '' }] })} data-testid="kpi-add-btn" className="text-xs text-blue-600 flex items-center gap-1"><Plus className="w-3 h-3" /> KPI</button>
      </div>
      <div className="space-y-2">
        {plan.kpis.map((k, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`kpi-row-${i}`}>
            <input value={k.name} onChange={e => update(i, { name: e.target.value })} placeholder="KPI" className={`${inputCls} col-span-12 sm:col-span-3`} data-testid={`kpi-name-${i}`} />
            <input type="number" value={k.target} onChange={e => update(i, { target: parseFloat(e.target.value) || 0 })} className={`${inputCls} col-span-5 sm:col-span-2 text-right`} data-testid={`kpi-target-${i}`} />
            <select value={k.unit} onChange={e => update(i, { unit: e.target.value })} className={`${inputCls} col-span-3 sm:col-span-1`} data-testid={`kpi-unit-${i}`}>{UNITS.map(u => <option key={u.v} value={u.v}>{u.l}</option>)}</select>
            <input value={k.basis || ''} onChange={e => update(i, { basis: e.target.value })} placeholder="Control basis" className={`${inputCls} col-span-12 sm:col-span-2`} />
            <input value={k.focus || ''} onChange={e => update(i, { focus: e.target.value })} placeholder="Quarterly review focus" className={`${inputCls} col-span-10 sm:col-span-3`} />
            <button onClick={() => setPlan({ ...plan, kpis: plan.kpis.filter((_, j) => j !== i) })} className="col-span-2 sm:col-span-1 p-1.5 text-slate-400 hover:text-red-600 justify-self-center" data-testid={`kpi-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const TargetEditor = ({ plan, setPlan, companies, bus, team }) => {
  const active = companies.filter(c => c.active);
  const cSum = active.reduce((s, c) => s + (plan.company_targets[c.id] || 0), 0);
  const set = (patch) => setPlan({ ...plan, ...patch });
  return (
    <div className="space-y-6" data-testid="target-editor">
      <div className="bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div><label className="text-xs font-medium text-slate-600">Group name</label><input value={plan.group_name || ''} onChange={e => set({ group_name: e.target.value })} className={inputCls} data-testid="group-name-input" /></div>
        <div><label className="text-xs font-medium text-slate-600">Group turnover target</label><CroreInput value={plan.group_target} onChange={v => set({ group_target: v })} testId="group-target-input" /></div>
        <div><label className="text-xs font-medium text-slate-600">Domestic</label><CroreInput value={plan.domestic_target} onChange={v => set({ domestic_target: v })} testId="domestic-target-input" /></div>
        <div><label className="text-xs font-medium text-slate-600">Export</label><CroreInput value={plan.export_target} onChange={v => set({ export_target: v })} testId="export-target-input" /></div>
        <div className="md:col-span-4"><QuarterInputs quarters={plan.group_quarters} target={plan.group_target} onChange={qs => set({ group_quarters: qs })} testPrefix="group" /></div>
        <div className="md:col-span-4 flex flex-wrap gap-2">
          <SumCheck target={plan.group_target} sum={cSum} label="Companies" testId="group-sumcheck" />
          {(plan.domestic_target || plan.export_target) ? <SumCheck target={plan.group_target} sum={(plan.domestic_target || 0) + (plan.export_target || 0)} label="Domestic + Export" testId="split-sumcheck" /> : null}
        </div>
      </div>

      {active.map(c => {
        const cBus = bus.filter(b => b.company_id === c.id && b.active);
        const bSum = cBus.reduce((s, b) => s + (plan.bu_targets[b.id]?.target || 0), 0);
        return (
          <div key={c.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`company-target-card-${c.id}`}>
            <div className="flex flex-wrap items-center gap-3 px-4 py-3 bg-slate-900 text-white">
              <p className="flex-1 font-semibold">{c.name} {c.code && <span className="ml-1 text-xs font-mono text-slate-400">{c.code}</span>}</p>
              <SumCheck target={plan.company_targets[c.id] || 0} sum={bSum} label="Business units" testId={`company-sumcheck-${c.id}`} />
              <CroreInput value={plan.company_targets[c.id] || 0} onChange={v => set({ company_targets: { ...plan.company_targets, [c.id]: v } })} className="w-40 text-slate-900" testId={`company-target-${c.id}`} />
            </div>
            <div className="px-4 py-2 bg-slate-100 border-b border-slate-200">
              <QuarterInputs quarters={(plan.company_quarters || {})[c.id]} target={plan.company_targets[c.id] || 0} onChange={qs => set({ company_quarters: { ...(plan.company_quarters || {}), [c.id]: qs } })} testPrefix={`company-${c.id}`} />
            </div>
            <BuTable company={c} bus={cBus} plan={plan} setPlan={setPlan} team={team} />
          </div>
        );
      })}
      {active.length === 0 && <p className="text-sm text-slate-500 text-center py-8">Add a company first in the Companies &amp; BUs tab.</p>}

      <KpiTable plan={plan} setPlan={setPlan} />
    </div>
  );
};
